import type { Track } from '@/world/Track';
import type { TrackTheme } from '@/world/Theme';
import type { City, LandmarkKind, RouteZone, SceneryKind } from '@/config/cities';
import { Rng } from '@/core/Rng';
import { emptyArrays, type SceneryArrays, type ZoneCtx } from './types';
import { ZONE_BUILDERS, RIVER_SIDE } from './zones';
import { CAB_YELLOW, CARS } from './palettes';
import { clampHills, carveLakeBasins } from './hills';
import { clampVegetationToShore, resolveWaterCfg } from './water';

/**
 * scenery/place.ts — the PURE placement pass (M98: extracted verbatim from
 * Scenery.buildScenery so it runs headless). Walks the track once per
 * (12-unit step × side), lets each zone builder push instances, then adds the
 * shared kerb-traffic pass. No three.js, no DOM — the corridor audit and the
 * cross-city layout regression tests drive this directly.
 *
 * DETERMINISM: a city's look is its seed + the exact SEQUENCE of rng calls in
 * here and in the zone builders. Don't reorder anything without expecting that
 * city's layout to reshuffle.
 */

/** Which scenery kind applies at this progress-to-finish (0..1). */
export function zoneKind(route: RouteZone[], progress: number): SceneryKind {
  for (const z of route) if (progress <= z.until) return z.kind;
  return route[route.length - 1].kind;
}

/** Zones that get the shared kerb-traffic pass below (the original NYC/London
 *  streets). New cities place their own kerb props inside their zone builders. */
const KERB_TRAFFIC_ZONES = new Set<SceneryKind>([
  'brownstones',
  'skyscrapers',
  'timessquare',
  'westminster',
  'thames',
]);

/** Run every zone builder along the course and return the instance arrays. */
export function placeScenery(track: Track, city: City, theme: TrackTheme): SceneryArrays {
  const rng = new Rng((city.seed ^ 0x5ce7e5) >>> 0);
  // A SEPARATE stream for crowds so people never disturb the scenery layout.
  const peopleRng = new Rng((city.seed ^ 0x9e3779b9) >>> 0);
  const route: RouteZone[] = city.route ?? [{ until: 1, kind: 'generic' }];
  const hw = track.halfWidth;
  const finish = track.finishDistance;
  const isLondon = city.id === 'london';

  // Landmark progress marks — zones use these for clearings (plazas, water gaps).
  const marks: Partial<Record<LandmarkKind, number>> = {};
  for (const lm of city.landmarks ?? []) marks[lm.kind] = lm.at;

  const a = emptyArrays();
  const STEP = 12;
  for (let dist = 6; dist < finish; dist += STEP) {
    const s = track.sampleAt(dist);
    const progress = dist / finish;
    const kind = zoneKind(route, progress);
    for (const side of [-1, 1] as const) {
      const at = (lateral: number): [number, number, number] => [
        s.x + s.rightX * side * lateral,
        s.y,
        s.z + s.rightZ * side * lateral,
      ];
      const ctx: ZoneCtx = {
        rng,
        peopleRng,
        s,
        side,
        progress,
        hw,
        step: STEP,
        theme,
        city,
        at,
        a,
        marks,
      };
      ZONE_BUILDERS[kind](ctx);

      // Kerb traffic for the original street zones: black cabs + the odd red
      // double-decker (London); yellow cabs (NYC). Never on the river side of a
      // Thames stretch (it'd float on the water).
      const onRiver = kind === 'thames' && side === RIVER_SIDE;
      if (KERB_TRAFFIC_ZONES.has(kind) && !onRiver && rng.next() < 0.18) {
        const [x, y, z] = at(hw + 2.4 + rng.range(0, 1.5));
        const taxi = isLondon ? 0x17181d : CAB_YELLOW;
        a.cabs.push({ x, y, z, rotY: s.dir, color: rng.next() < 0.66 ? taxi : rng.pick(CARS) });
      }
      if (
        isLondon &&
        !onRiver &&
        (kind === 'westminster' || kind === 'skyscrapers') &&
        rng.next() < 0.08
      ) {
        const [x, y, z] = at(hw + 3 + rng.range(0, 1.2));
        a.buses.push({ x, y, z, rotY: s.dir });
      }
      if (
        isLondon &&
        !onRiver &&
        (kind === 'westminster' || kind === 'skyscrapers') &&
        rng.next() < 0.06
      ) {
        const [x, y, z] = at(hw + 1.8);
        a.phoneBoxes.push({ x, y, z, rotY: s.dir });
      }
    }
  }
  // M98: the track-aware hill pass — a dome clamped clear of ITS stretch can
  // still bulge over a later pass of the looping course; push or drop those.
  clampHills(a.hills, track);
  // nyc-lake: an AUTHORED lake carves its own basin — domes standing in the
  // water are trimmed or walked out (never deleted, never onto the road) and
  // swallowed trees move to the bank. After clampHills on purpose: the road
  // guarantee is the one it must not undo, and it re-checks every dome it
  // moves against it.
  carveLakeBasins(a, city.water?.lakes ?? [], track);
  // ...and then put back anything that pass (or any other) left standing in
  // open water. carveLakeBasins pushes a swallowed tree OUT to the lake rim,
  // which stopped meaning "land" the moment NYC's park apron became a welded
  // reservoir. Lateral-only, no rng — see clampVegetationToShore.
  clampVegetationToShore(
    [a.trees, a.palms, a.pines, a.baobabs],
    a.ponds,
    track,
    resolveWaterCfg(city.water),
  );
  // NOTE (tree round): the vegetation UN-BURIAL pass (hills.liftOntoHills) is
  // deliberately NOT here — it must run after the finish/shortcut clearings
  // that DELETE domes, so Scenery.buildScenery owns the call. See the comment
  // there. This function's output is therefore still "everything at road
  // grade", which is exactly what the placement audits/tests measure.
  return a;
}
